/**
 * PrpWorkspaceLayout - the Psychiatric Rehabilitation Program workspace shell.
 * Own dedicated sidebar (like Billing / Settings), sharing the app's header
 * primitives (NotificationBell + UserMenu). Kept separate from the medical
 * patient chart so PRP staff work in PRP screens only. On phones the sidebar
 * gives way to a top bar, bottom tab bar and slide-out menu (mobile-shell.tsx).
 */
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { PRP_SIDEBAR_HEADER, prpWorkspaceNav } from '@/components/layout/nav/prp-nav';
import { usePermissions } from '@/hooks/use-permissions';
import { usePrpMode } from '@/hooks/use-prp-mode';
import {
  BrandWordmark,
  NotificationBell,
  ShellPageTransition,
  ShellSidebar,
  UserMenu,
} from '@/components/layout/shell';
import { ThemePicker } from '@/components/shared/theme-picker';
import type { ShellNavItem } from '@/components/layout/shell';
import { useAuth } from '@/hooks/use-auth';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { PrpMobileMenu, PrpMobileTabBar, PrpMobileTopBar } from './mobile-shell';
import { readDocumentOrigin } from './navigation';

function PrpSidebarHeader({ collapsed }: { collapsed: boolean }) {
  return (
    <motion.div
      key={collapsed ? 'logo-mini' : 'logo-full'}
      initial={{ opacity: 0, x: collapsed ? 0 : -12, scale: collapsed ? 0.85 : 1 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: collapsed ? 0.85 : 1 }}
      transition={{ duration: 0.18 }}
      className="w-full"
    >
      <BrandWordmark collapsed={collapsed} tone="dark" subtitle={PRP_SIDEBAR_HEADER.subtitle} />
    </motion.div>
  );
}

const CRUMBS: Record<string, string> = {
  '': 'Dashboard',
  caseload: 'Caseload',
  clients: 'Client',
  documents: 'Documents',
  'contact-notes': 'Contact Notes',
  referrals: 'Referrals',
  intakes: 'Referral',
  authorizations: 'Authorizations',
  irps: 'IRPs',
  dla20: 'DLA-20',
  'monthly-summaries': 'Monthly Summaries',
  reviews: 'Pending Sign-offs',
  compliance: 'Compliance',
  tasks: 'My Tasks',
  messages: 'Messages',
  settings: 'Program Settings',
};

/** Shared (non-PRP) pages shown inside the PRP shell in PRP mode. */
const SHARED_CRUMBS: Record<string, string> = {
  schedule: 'Schedule',
  'my-day': 'My Day',
  groups: 'Groups',
  tasks: 'Tasks',
  messages: 'Messages',
  patients: 'Client',
  billing: 'Billing',
  settings: 'Settings',
  'settings/users': 'Users',
  'settings/locations': 'Locations',
  'settings/prp': 'Program Settings',
  'settings/organization': 'Practice Info',
  'settings/security': 'Security',
  'settings/audit': 'Audit Logs',
  'settings/roles': 'Roles & Permissions',
};

function crumbFor(pathname: string): string {
  if (!pathname.startsWith('/prp')) {
    const [first = '', second = ''] = pathname.replace(/^\//, '').split('/');
    return SHARED_CRUMBS[`${first}/${second}`] ?? SHARED_CRUMBS[first] ?? first;
  }
  const seg = pathname.replace(/^\/prp\/?/, '').split('/')[0] ?? '';
  return seg === 'documents' && pathname.startsWith('/prp/documents/')
    ? 'Document'
    : (CRUMBS[seg] ?? seg);
}

function PrpTopBar() {
  const location = useLocation();
  const origin = readDocumentOrigin(location.state);
  const onDocument = location.pathname.startsWith('/prp/documents/');
  const crumb = crumbFor(location.pathname);
  return (
    // relative z-40 (as in PracticeLayout) keeps the notification and user
    // menus above the page content, which forms its own stacking layers.
    <header className="relative z-40 flex h-16 shrink-0 items-center justify-between border-b border-border bg-card/95 backdrop-blur-xl px-8">
      <div className="flex items-center gap-1.5 text-sm">
        <span className="font-medium text-muted-foreground">PRP</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
        {onDocument && origin && (
          <>
            <span className="font-medium text-muted-foreground">{origin.label}</span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
          </>
        )}
        <span className="font-bold text-foreground">{crumb}</span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <ThemePicker variant="icon" />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

function prpItemActive(pathname: string, item: ShellNavItem, source?: string): boolean {
  if (pathname.startsWith('/prp/documents/') && source) {
    if (source.startsWith('/prp/clients/')) return item.href === '/prp/caseload';
    return item.href === source;
  }
  if (item.href === '/prp') return pathname === '/prp' || pathname === '/prp/';
  // "All Settings" is the Settings home only; its sub-pages have their own items.
  if (item.href === '/settings') return pathname === '/settings';
  // An intake lives under Referrals.
  if (item.href === '/prp/referrals' && pathname.startsWith('/prp/intakes/')) return true;
  // A client chart lives under Caseload.
  if (item.href === '/prp/caseload' && pathname.startsWith('/prp/clients/')) return true;
  return pathname === item.href || pathname.startsWith(item.href + '/');
}

export default function PrpWorkspaceLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const origin = readDocumentOrigin(location.state);
  const { user } = useAuth();
  // In PRP mode this workspace is the whole app, so there is nothing to go
  // back to; full-EHR staff visiting PRP get "Back to App".
  const { prpMode, prpOnlyOrg } = usePrpMode();
  const { can } = usePermissions();
  const admin = user?.role === 'super_admin' || user?.role === 'org_admin';
  const navSections = prpWorkspaceNav({ prpOnlyOrg, admin, can });
  const showBackToApp = !prpMode;
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const isActive = (pathname: string, item: ShellNavItem) =>
    prpItemActive(pathname, item, origin?.pathname);

  if (isMobile) {
    const onDocument = location.pathname.startsWith('/prp/documents/');
    return (
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-background">
        <PrpMobileTopBar title={crumbFor(location.pathname)} onMenu={() => setMenuOpen(true)} />
        <main className="relative flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full px-4 pb-28 pt-4">
            <ShellPageTransition>
              <Outlet />
            </ShellPageTransition>
          </div>
        </main>
        {!onDocument && (
          <PrpMobileTabBar
            isActive={(href) => isActive(location.pathname, { href } as ShellNavItem)}
            onMenu={() => setMenuOpen(true)}
          />
        )}
        <PrpMobileMenu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          isItemActive={isActive}
          backToApp={showBackToApp}
          sections={navSections}
        />
      </div>
    );
  }

  return (
    <div className="app-shell relative flex h-screen overflow-hidden bg-background">
      <ShellSidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        sections={navSections}
        header={<PrpSidebarHeader collapsed={collapsed} />}
        backLink={showBackToApp ? { label: 'Back to App', onClick: () => navigate('/') } : undefined}
        isItemActive={isActive}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <PrpTopBar />
        <main className="relative flex-1 overflow-y-auto">
          <div className="p-8 pb-20 w-full max-w-[1600px] mx-auto">
            <ShellPageTransition>
              <Outlet />
            </ShellPageTransition>
          </div>
        </main>
      </div>
    </div>
  );
}
